import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { SectionHeading } from "@/components/site/SectionHeading";
import { VideoBackdrop } from "@/components/site/VideoBackdrop";

const EMPTY = { name: "", age: "", discord: "", availability: "", role_wanted: "", experience: "", motivation: "", website: "" };

function Input({ id, label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-sm font-medium text-eden-text">{label}</label>
      {children}
      {hint && <p className="text-xs text-eden-muted">{hint}</p>}
    </div>
  );
}

export default function Candidatura() {
  const { content } = useContent();
  const staff = content.staff || {};
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const open = staff.apply_open !== false && (staff.apply_mode || "form") === "form";

  const submit = async (e) => {
    e.preventDefault();
    if (form.motivation.trim().length < 20) { toast.error("Conta-nos um pouco mais sobre a tua motivação (mínimo 20 caracteres)."); return; }
    setBusy(true);
    try {
      await api.post("/applications", { ...form, age: Number(form.age) });
      setDone(true);
    } catch (err) {
      toast.error(errorMessage(err, "Não foi possível enviar a candidatura."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative py-16 sm:py-24" aria-labelledby="apply-title" data-testid="apply-page">
      <VideoBackdrop opacity={0.22} className="h-[420px]" />
      <div className="container-eden relative max-w-3xl">
        <Link to="/informacoes" className="inline-flex items-center gap-1.5 text-sm text-eden-secondary hover:text-eden-text" data-testid="apply-back"><ArrowLeft size={14} /> Voltar às informações</Link>
        <div className="mt-6">
          <SectionHeading id="apply-title" eyebrow="Candidaturas" title={staff.apply_label || "Junta-te à equipa"} text={staff.apply_text || "Preenche o formulário e a equipa entra em contacto contigo pelo Discord."} />
        </div>

        {!open && <div className="card-eden mt-12 p-8 text-center" data-testid="apply-closed"><p className="text-eden-secondary">{staff.apply_closed_text || "As candidaturas estão fechadas de momento."}</p></div>}

        {open && done && (
          <div className="card-eden mt-12 flex flex-col items-center p-10 text-center animate-fade-up" role="status" data-testid="apply-success">
            <CheckCircle2 size={40} className="text-emerald-400" aria-hidden="true" />
            <h2 className="mt-4 font-display text-xl font-bold text-eden-text">Candidatura enviada!</h2>
            <p className="mt-2 max-w-md text-sm text-eden-secondary">Obrigado, {form.name.split(" ")[0]}. A equipa vai analisar a tua candidatura e responde-te no Discord ({form.discord}).</p>
            <Link to="/" className="btn-secondary mt-8 h-10">Voltar ao início</Link>
          </div>
        )}

        {open && !done && (
          <form onSubmit={submit} className="card-eden mt-12 space-y-6 p-6 sm:p-8" data-testid="apply-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <Input id="apply-name" label="Nome (ou nome na cidade)"><input id="apply-name" required minLength={2} maxLength={60} value={form.name} onChange={set("name")} className="input-eden" data-testid="apply-name" /></Input>
              <Input id="apply-age" label="Idade"><input id="apply-age" type="number" required min={13} max={99} value={form.age} onChange={set("age")} className="input-eden" data-testid="apply-age" /></Input>
              <Input id="apply-discord" label="Utilizador Discord" hint="Ex.: karma_eden"><input id="apply-discord" required minLength={2} maxLength={60} value={form.discord} onChange={set("discord")} className="input-eden" data-testid="apply-discord" /></Input>
              <Input id="apply-availability" label="Disponibilidade" hint="Ex.: 3 a 4 horas por dia, mais à noite"><input id="apply-availability" required minLength={2} maxLength={120} value={form.availability} onChange={set("availability")} className="input-eden" data-testid="apply-availability" /></Input>
            </div>
            <Input id="apply-role" label="Função pretendida (opcional)" hint="Ex.: Helper, Moderador, Developer"><input id="apply-role" maxLength={60} value={form.role_wanted} onChange={set("role_wanted")} className="input-eden" data-testid="apply-role" /></Input>
            <Input id="apply-experience" label="Experiência anterior (opcional)"><textarea id="apply-experience" rows={3} maxLength={1500} value={form.experience} onChange={set("experience")} className="input-eden min-h-[90px]" data-testid="apply-experience" /></Input>
            <Input id="apply-motivation" label="Porque queres fazer parte da staff do EDEN?" hint={`${form.motivation.length}/2000 · mínimo 20 caracteres`}><textarea id="apply-motivation" required minLength={20} rows={5} maxLength={2000} value={form.motivation} onChange={set("motivation")} className="input-eden min-h-[140px]" data-testid="apply-motivation" /></Input>
            <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-eden-muted">Os teus dados são usados apenas para avaliar a candidatura.</p>
              <button type="submit" disabled={busy} className="btn-primary h-11" data-testid="apply-submit">{busy ? "A enviar…" : "Enviar candidatura"} <Send size={15} aria-hidden="true" /></button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
